import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/AuthContext';
import { ApiError } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Footer } from '@/components/Footer';
import { inputClass } from '@/components/ui/Field';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(username.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setError(t('login.invalid'));
      else if (err instanceof ApiError) setError(err.message);
      else setError(t('errors.network'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-conduril p-6">
      <div className="relative flex w-full max-w-sm flex-col gap-4 rounded-2xl bg-white p-7 shadow-2xl">
        <div className="absolute right-4 top-4">
          <LanguageSwitcher />
        </div>
        <img src="/logo.svg" alt="Guias por Obra" className="mx-auto h-14" />
        <h1 className="text-center text-lg font-bold text-gray-900">{t('login.title')}</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <label className="flex flex-col gap-1 text-[13px] font-semibold text-gray-800">
            {t('login.username')}
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoCapitalize="none"
              autoComplete="username"
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-[13px] font-semibold text-gray-800">
            {t('login.password')}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className={inputClass}
            />
          </label>

          <ErrorBanner message={error} />

          <Button type="submit" disabled={submitting} className="w-full">
            {t('login.submit')}
          </Button>
        </form>
      </div>
      <Footer onDark />
    </div>
  );
}
